import { buildingForEachEntityOfType } from "building-common/buildingForEachEntity";
import { wallLayerFacesByRoom } from "building-generation/elements/walls/wallFaces";
import type { Building } from "building-types/building/buildingTypes";
import type {
  WallInteriorFinishKAM,
  WallInteriorFinishKAMOptions,
  WallInteriorFinishKeyAttributes,
  WallInteriorFinishKeyMeasures,
} from "building-types/dataSequences/builtin/WallInteriorFInishKAM";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import { buildingEntityPickHandle } from "building-types/entities/buildingEntityPickHandle";
import type { SolidWallLayer } from "building-types/walls/wallTypes";
import {
  keyAttributeNew,
  keyAttributeOptionsNew,
  keyMeasureNew,
  keyMeasureOptionsNew,
} from "doc/utils/dataSequenceTools";
import type { Face } from "geom/brep";
import { polyLine2AABB2 } from "geom/polyLine2";
import { unionCoplanarFaces } from "geom/unionFaces";

/** @deprecated - use the Building Query API instead */
export function buildingWallInteriorFinishKAMs(
  building: Building,
  filter?: BuildingEntityFilter,
): WallInteriorFinishKAM[] {
  const wallAreas: WallInteriorFinishKAM[] = [];

  const facesByRoom: { [roomId: string]: Face[] } = {};
  buildingForEachEntityOfType(building, "WALL", (entity) => {
    wallLayerFacesByRoom(entity as unknown as SolidWallLayer, facesByRoom);
  });

  for (const roomId in facesByRoom) {
    const faces = facesByRoom[roomId];
    const room = building.rooms[roomId];

    if (filter && !filter(room)) {
      continue;
    }

    const level = room.level;

    const unioned = unionCoplanarFaces(faces);

    for (let i = 0; i < unioned.length; i++) {
      const face = unioned[i];
      const aabb2 = polyLine2AABB2(face.loops[0]);

      const length: number = Math.round(((aabb2.maxX - aabb2.minX) / 12) * 100) / 100;
      const height: number = Math.round(((aabb2.maxY - aabb2.minY) / 12) * 100) / 100;

      wallAreas.push({
        type: "INTERIOR_FINISH",
        attributes: {
          levelName: keyAttributeNew(level.name),
          height: keyAttributeNew(`${height}`, "FEET"),
          levelId: keyAttributeNew(level.id),
          roomId: keyAttributeNew(room.id),
          roomName: keyAttributeNew(room.name),
          wallNumber: keyAttributeNew(`${i + 1}`),
        },
        measures: {
          length: keyMeasureNew(length, "FEET"),
          height: keyMeasureNew(height, "FEET"),
        },
        handles: [buildingEntityPickHandle(room)],
      });
    }
  }

  return wallAreas;
}

export const EMPTY_WALL_INTERIOR_FINISH_KAM: WallInteriorFinishKAM = {
  type: "INTERIOR_FINISH",
  attributes: {
    levelName: keyAttributeNew(""),
    levelId: keyAttributeNew(""),
    roomId: keyAttributeNew(""),
    roomName: keyAttributeNew(""),
    wallNumber: keyAttributeNew(""),
    height: keyAttributeNew("", "FEET"),
  },
  measures: {
    length: keyMeasureNew(0, "FEET"),
    height: keyMeasureNew(0, "FEET"),
  },
  handles: [],
};

export function defaultWallInteriorFinishKAMSort(a: WallInteriorFinishKAM, b: WallInteriorFinishKAM) {
  return (
    a.attributes.roomId.value.localeCompare(b.attributes.roomId.value) ||
    a.attributes.roomName.value.localeCompare(b.attributes.roomName.value) ||
    a.attributes.wallNumber.value.localeCompare(b.attributes.wallNumber.value)
  );
}

export function makeDefaultWallInteriorFinishKAMOptions(): WallInteriorFinishKAMOptions {
  return {
    attributes: {
      roomId: keyAttributeOptionsNew({ definesEntity: false }),
      roomName: keyAttributeOptionsNew({ definesEntity: false }),
      wallNumber: keyAttributeOptionsNew({ definesEntity: true }),
    },
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
      height: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}

export const DEFINES_WALL_INTERIOR_FINISH: (keyof WallInteriorFinishKeyAttributes)[] = ["levelId", "height"];

export const WALL_INTERIOR_FINISH_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  WallInteriorFinishKeyAttributes,
  WallInteriorFinishKeyMeasures
> = {
  attributes: {
    levelName: {
      hidden: false,
    },
    levelId: {
      hidden: true,
    },
    height: {
      hidden: false,
    },
    roomId: {
      hidden: true,
    },
    roomName: {
      hidden: false,
    },
    wallNumber: {
      hidden: true,
    },
  },
  measures: {
    length: {
      hidden: false,
    },
    height: {
      hidden: false,
    },
  },
};
